import { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { Product, ProductTag } from '../../../types/product.types';
import {
  UploadsService,
  CategoriesService,
  TagsService,
  MaterialsService,
  ColorsService,
  SuppliersService,
} from '../../../api';
import { CreateCategoryDto } from '../../../api/models/CreateCategoryDto';
import { CreateTagDto } from '../../../api/models/CreateTagDto';
import { CreateMaterialDto } from '../../../api/models/CreateMaterialDto';
import { CreateColorDto } from '../../../api/models/CreateColorDto';
import { CreateSupplierDto } from '../../../api/models/CreateSupplierDto';
import { normalizeTags, removeDuplicateTags, tagArrayToIdList } from '../../../utils/helpers';
import { useAsyncPaginatedCatalog } from '../../../hooks/useAsyncPaginatedCatalog';
import {
  fetchCategoriesPage,
  fetchMaterialsPage,
  fetchColorsPage,
  fetchSuppliersPage,
  fetchTagsPage,
  type CatalogTagRow,
} from '../../../utils/catalogPageFetchers';
import { useProductCostEntry } from '../useProductCostEntry';
import { toFormNumberString } from '../productCostPricing.utils';
import type { CostEntryMode } from '../productCostPricing.utils';

export interface UseProductDrawerReturn {
  // Step management
  currentStep: number;
  formProgress: number;
  setStep: (step: number) => void;
  goNext: () => void;
  goPrev: () => void;

  // Form data
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  selectedTags: ProductTag[];
  setSelectedTags: React.Dispatch<React.SetStateAction<ProductTag[]>>;

  // Cost entry
  costEntryMode: CostEntryMode;
  costPerGram: string;
  handleCostEntryModeChange: (next: CostEntryMode) => void;
  handleCostPriceChange: (val: number | null, weight: string) => void;
  handleCostPerGramChange: (val: number | null, weight: string) => void;
  handleWeightChange: (
    val: number | null,
    mode: CostEntryMode,
    currentCostPrice: string,
    currentCostPerGram: string,
  ) => void;
  hasWeight: (weight: string) => boolean;

  // Image handling
  image: File | null;
  setImage: React.Dispatch<React.SetStateAction<File | null>>;
  imageUrl: string;
  setImageUrl: React.Dispatch<React.SetStateAction<string>>;
  imageError: string | null;
  setImageError: React.Dispatch<React.SetStateAction<string | null>>;
  isUploading: boolean;
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
  isDraggingImage: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;

  // Image modal
  showImageModal: boolean;
  setShowImageModal: React.Dispatch<React.SetStateAction<boolean>>;

  // UI state
  showConfirmation: boolean;
  setShowConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  showCancelConfirmation: boolean;
  setShowCancelConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  isSaving: boolean;
  setIsSaving: React.Dispatch<React.SetStateAction<boolean>>;

  // Add Category Modal
  showAddCategoryModal: boolean;
  setShowAddCategoryModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleOpenAddCategoryModal: () => void;
  handleCloseAddCategoryModal: () => void;
  handleSaveCategory: (data: { nombre: string; descripcion: string }) => Promise<void>;

  // Add Material Modal
  showAddMaterialModal: boolean;
  setShowAddMaterialModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleOpenAddMaterialModal: () => void;
  handleCloseAddMaterialModal: () => void;
  handleSaveMaterial: (data: { nombre: string; descripcion: string }) => Promise<void>;
  showAddColorModal: boolean;
  setShowAddColorModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleOpenAddColorModal: () => void;
  handleCloseAddColorModal: () => void;
  handleSaveColor: (data: { nombre: string; descripcion: string }) => Promise<void>;

  // Add Supplier Modal
  showAddSupplierModal: boolean;
  setShowAddSupplierModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleOpenAddSupplierModal: () => void;
  handleCloseAddSupplierModal: () => void;
  handleSaveSupplier: (data: {
    nombre: string;
    contacto: string;
    telefono: string;
    email: string;
    descripcion: string;
  }) => Promise<void>;

  // Add Tag Modal
  showAddTagModal: boolean;
  setShowAddTagModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleOpenAddTagModal: () => void;
  handleCloseAddTagModal: () => void;
  handleSaveTag: (data: { nombre: string; descripcion: string; color: string }) => Promise<void>;

  // Tag error state
  tagError: string | null;
  setTagError: React.Dispatch<React.SetStateAction<string | null>>;

  categoryCatalog: ReturnType<typeof useAsyncPaginatedCatalog<{ id: number; name: string }>>;
  tagsCatalog: ReturnType<typeof useAsyncPaginatedCatalog<CatalogTagRow>>;
  materialCatalog: ReturnType<typeof useAsyncPaginatedCatalog<{ id: number; name: string }>>;
  colorCatalog: ReturnType<typeof useAsyncPaginatedCatalog<{ id: number; name: string }>>;
  supplierCatalog: ReturnType<typeof useAsyncPaginatedCatalog<{ id: number; name: string }>>;

  // Handlers
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSelectChange: (e: any) => void;
  handleSelectedTagsChange: (tags: ProductTag[]) => void;
  handleImageSelect: () => void;
  handleImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleImageDragEnter: (e: React.DragEvent) => void;
  handleImageDragLeave: (e: React.DragEvent) => void;
  handleImageDragOver: (e: React.DragEvent) => void;
  handleImageDrop: (e: React.DragEvent) => void;
  handleImageUpload: () => Promise<{
    imageUrl: string | null;
    hasThumbnail: boolean;
  }>;
  handleRemoveImage: () => void;
  handleImageClick: () => void;
  validateCurrentStep: () => boolean;
  handleSave: () => Promise<void>;
  handleCloseClick: () => void;
  cancelOperation: () => void;
}

export const useProductDrawer = (
  isOpen: boolean,
  onClose: () => void,
  onSave: (product: any) => Promise<void>,
  editProduct?: Product | null,
  onCategoryAdded?: (newCategoryId: number) => void,
  onTagAdded?: (newTagId: number) => void,
  availableTags?: ProductTag[],
  initialProductTypes?: { id: number; name: string }[],
  initialProductMaterials?: { id: number; name: string }[],
  initialProductColors?: { id: number; name: string }[],
  onMaterialAdded?: (newMaterialId: number) => void,
  onColorAdded?: (newColorId: number) => void,
  initialProductSuppliers?: { id: number; name: string }[],
  onSupplierAdded?: (newSupplierId: number) => void,
  prefillDuplicateFrom?: Product | null,
): UseProductDrawerReturn => {
  // Step management
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [formProgress, setFormProgress] = useState<number>(20);

  // UI state
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Add Category Modal
  const [showAddCategoryModal, setShowAddCategoryModal] = useState<boolean>(false);

  // Add Material Modal
  const [showAddMaterialModal, setShowAddMaterialModal] = useState<boolean>(false);
  const [showAddColorModal, setShowAddColorModal] = useState<boolean>(false);

  // Add Supplier Modal
  const [showAddSupplierModal, setShowAddSupplierModal] = useState<boolean>(false);

  // Add Tag Modal
  const [showAddTagModal, setShowAddTagModal] = useState<boolean>(false);

  // Tags
  const [selectedTags, setSelectedTags] = useState<ProductTag[]>([]);
  const [tagError, setTagError] = useState<string | null>(null);

  const tagPins = useMemo((): CatalogTagRow[] => {
    return selectedTags
      .filter(
        (t): t is ProductTag & { id: number } => typeof t.id === 'number' && !Number.isNaN(t.id),
      )
      .map((t) => ({ id: t.id, name: t.name, color: t.color }));
  }, [selectedTags]);

  const tagsCatalog = useAsyncPaginatedCatalog<CatalogTagRow>({
    isActive: isOpen,
    fetchPage: fetchTagsPage,
    pageSize: 10,
    pinItems: tagPins,
  });

  // Image handling
  const [image, setImage] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [imageError, setImageError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isDraggingImage, setIsDraggingImage] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageDragCounterRef = useRef(0);

  // Image modal
  const [showImageModal, setShowImageModal] = useState<boolean>(false);

  // Refs to track drawer state and prevent unnecessary step resets
  const previousIsOpen = useRef<boolean>(false);
  const previousEditProductId = useRef<number | null>(null);
  const previousPrefillDuplicateId = useRef<number | null>(null);
  /** Evita doble POST por doble clic en confirmar (bloqueo síncrono antes del await). */
  const saveInFlightRef = useRef(false);

  // Form data
  const [formData, setFormData] = useState({
    id: null as number | null,
    name: '',
    code: '',
    description: '',
    status: 'ACTIVE',
    type: '',
    material: '',
    weight: '',
    length: '',
    diameter: '',
    color: '',
    colorId: '',
    workshopCode: '',
    costPrice: '',
    sellingPrice: '',
    supplier: '',
    imagePath: '',
    hasThumbnail: false,
  });

  const {
    costEntryMode,
    costPerGram,
    resetCostEntry,
    initCostEntryFromPiece,
    handleCostEntryModeChange,
    handleCostPriceChange,
    handleCostPerGramChange,
    handleWeightChange,
    isPricingStepValid,
    hasWeight,
  } = useProductCostEntry(setFormData);

  const categoryPins = useMemo((): { id: number; name: string }[] => {
    const id = Number(formData.type);
    if (!id || Number.isNaN(id)) return [];
    const fromInitial = initialProductTypes?.find((t) => t.id === id);
    if (fromInitial) return [fromInitial];
    const ep = editProduct as Product & { category?: { id: number; name: string } };
    if (ep?.category?.id === id && ep.category.name) return [{ id, name: ep.category.name }];
    if (ep?.categoryId === id && typeof ep.type === 'string' && ep.type.trim()) {
      return [{ id, name: ep.type }];
    }
    const dup = prefillDuplicateFrom;
    if (dup?.categoryId === id && typeof dup.type === 'string' && dup.type.trim()) {
      return [{ id, name: dup.type }];
    }
    return [{ id, name: `Tipo #${id}` }];
  }, [formData.type, editProduct, prefillDuplicateFrom, initialProductTypes]);

  const materialPins = useMemo((): { id: number; name: string }[] => {
    const id = Number(formData.material);
    if (!id || Number.isNaN(id)) return [];
    const fromInitial = initialProductMaterials?.find((m) => m.id === id);
    if (fromInitial) return [fromInitial];
    const ep = editProduct as Product & { material?: string | { id: number; name: string } };
    if (ep) {
      const mat = ep.material;
      if (mat && typeof mat === 'object' && 'id' in mat) {
        const mobj = mat as { id: number; name: string };
        if (mobj.id === id) return [{ id, name: mobj.name }];
      }
      if (typeof mat === 'string' && mat.trim() && ep.materialId === id) {
        return [{ id, name: mat }];
      }
    }
    const dup = prefillDuplicateFrom;
    if (dup && dup.materialId === id && typeof dup.material === 'string' && dup.material.trim()) {
      return [{ id, name: dup.material.trim() }];
    }
    return [{ id, name: `Material #${id}` }];
  }, [formData.material, editProduct, prefillDuplicateFrom, initialProductMaterials]);

  const supplierPins = useMemo((): { id: number; name: string }[] => {
    const id = Number(formData.supplier);
    if (!id || Number.isNaN(id)) return [];
    const fromInitial = initialProductSuppliers?.find((s) => s.id === id);
    if (fromInitial) return [fromInitial];
    const ep = editProduct as Product & { supplier?: string | { id: number; name: string } };
    if (ep) {
      const sup = ep.supplier;
      if (sup && typeof sup === 'object' && 'id' in sup) {
        const sobj = sup as { id: number; name: string };
        if (sobj.id === id) return [{ id, name: sobj.name }];
      }
      if (typeof sup === 'string' && sup.trim() && ep.supplierId === id) {
        return [{ id, name: sup }];
      }
    }
    const dup = prefillDuplicateFrom;
    if (dup && dup.supplierId === id && typeof dup.supplier === 'string' && dup.supplier.trim()) {
      return [{ id, name: dup.supplier.trim() }];
    }
    return [{ id, name: `Proveedor #${id}` }];
  }, [formData.supplier, editProduct, prefillDuplicateFrom, initialProductSuppliers]);

  const colorPins = useMemo((): { id: number; name: string }[] => {
    const id = Number(formData.colorId);
    if (!id || Number.isNaN(id)) return [];
    const fromInitial = initialProductColors?.find((c) => c.id === id);
    if (fromInitial) return [fromInitial];
    const raw = (editProduct ?? prefillDuplicateFrom) as Product & { color?: string };
    if (typeof raw?.color === 'string' && raw.color.trim()) {
      return [{ id, name: raw.color.trim() }];
    }
    return [{ id, name: `Color #${id}` }];
  }, [formData.colorId, initialProductColors, editProduct, prefillDuplicateFrom]);

  const categoryCatalog = useAsyncPaginatedCatalog({
    isActive: isOpen,
    fetchPage: fetchCategoriesPage,
    pageSize: 10,
    pinItems: categoryPins,
  });

  const materialCatalog = useAsyncPaginatedCatalog({
    isActive: isOpen,
    fetchPage: fetchMaterialsPage,
    pageSize: 10,
    pinItems: materialPins,
  });

  const colorCatalog = useAsyncPaginatedCatalog({
    isActive: isOpen,
    fetchPage: fetchColorsPage,
    pageSize: 10,
    pinItems: colorPins,
  });

  const supplierCatalog = useAsyncPaginatedCatalog({
    isActive: isOpen,
    fetchPage: fetchSuppliersPage,
    pageSize: 10,
    pinItems: supplierPins,
  });

  // Initialize form data when drawer opens
  useEffect(() => {
    // Detect if drawer is opening (transitioning from closed to open)
    const isOpening = !previousIsOpen.current && isOpen;
    // Detect if it's a different product being edited
    const isNewProduct = isOpen && editProduct && editProduct.id !== previousEditProductId.current;
    const isNewDuplicateSource =
      isOpen &&
      prefillDuplicateFrom &&
      prefillDuplicateFrom.id !== previousPrefillDuplicateId.current;

    if (isOpen) {
      if (prefillDuplicateFrom) {
        const shouldResetDuplicateForm = isOpening || isNewDuplicateSource;
        if (shouldResetDuplicateForm) {
          setCurrentStep(1);
          setFormProgress(20);
          previousPrefillDuplicateId.current = prefillDuplicateFrom.id;
        }

        if (shouldResetDuplicateForm) {
          const src = prefillDuplicateFrom;
          const baseName = src.name?.trim() || '';
          const copyLabel = ' (copia)';
          const displayName = baseName.endsWith(copyLabel) ? baseName : `${baseName}${copyLabel}`;
          const thumb = (src as Product & { thumbnailUrl?: string | null }).thumbnailUrl;
          const imageForPreview = src.imagePath || thumb || '';

          const typeName = typeof src.type === 'string' && src.type.trim() ? src.type.trim() : '';
          const typeByNameId =
            src.categoryId == null && typeName
              ? initialProductTypes?.find((t) => t.name.trim() === typeName)?.id
              : undefined;
          const typeValue =
            src.categoryId != null
              ? String(src.categoryId)
              : typeByNameId != null
                ? String(typeByNameId)
                : '';

          const materialName =
            typeof src.material === 'string' && src.material.trim() ? src.material.trim() : '';
          const materialByNameId =
            src.materialId == null && materialName
              ? initialProductMaterials?.find((m) => m.name.trim() === materialName)?.id
              : undefined;
          const materialValue =
            src.materialId != null
              ? String(src.materialId)
              : materialByNameId != null
                ? String(materialByNameId)
                : '';

          const supplierName =
            typeof src.supplier === 'string' && src.supplier.trim() ? src.supplier.trim() : '';
          const supplierByNameId =
            src.supplierId == null && supplierName
              ? initialProductSuppliers?.find((s) => s.name.trim() === supplierName)?.id
              : undefined;
          const supplierValue =
            src.supplierId != null
              ? String(src.supplierId)
              : supplierByNameId != null
                ? String(supplierByNameId)
                : '';

          const dupWeight = toFormNumberString(src.weight);
          const dupCost = toFormNumberString(src.costPrice);
          const dupSale = toFormNumberString(src.sellingPrice);
          setFormData({
            id: null,
            name: displayName,
            code: '',
            description: src.description || '',
            status: src.status,
            type: typeValue,
            material: materialValue,
            weight: dupWeight,
            length:
              typeof src.length === 'number' ? src.length.toString() : src.length?.toString() || '',
            diameter: src.diameter || '',
            color: src.color || '',
            colorId: initialProductColors?.find((c) => c.name === src.color)?.id?.toString() ?? '',
            workshopCode: src.workshopCode || '',
            costPrice: dupCost,
            sellingPrice: dupSale !== '' ? dupSale : toFormNumberString(src.price),
            supplier: supplierValue,
            imagePath: src.imagePath || '',
            hasThumbnail: src.hasThumbnail === true,
          });
          initCostEntryFromPiece(dupCost, dupWeight);
          setSelectedTags(normalizeTags(src.tags));
          setImage(null);
          setImageUrl(imageForPreview);
        }
      } else if (editProduct) {
        // Only reset step when drawer is first opened or when editing a different product
        if (isOpening || isNewProduct) {
          setCurrentStep(1);
          setFormProgress(20);
          previousEditProductId.current = editProduct.id;
        }

        // Only update formData if this is a new opening or different product
        // This prevents resetting form data when only tags or other related data changes
        if (isOpening || isNewProduct) {
          const editWeight = toFormNumberString(editProduct.weight);
          const editCost = toFormNumberString(editProduct.costPrice);
          setFormData({
            id: editProduct.id,
            name: editProduct.name || '',
            code: editProduct.code || '',
            description: editProduct.description || '',
            status: editProduct.status,
            type: editProduct.categoryId?.toString() || editProduct.type?.toString() || '',
            material: editProduct.materialId?.toString() || editProduct.material?.toString() || '',
            weight: editWeight,
            length:
              typeof editProduct.length === 'number'
                ? editProduct.length.toString()
                : editProduct.length || '',
            diameter: editProduct.diameter || '',
            color: editProduct.color || '',
            colorId:
              initialProductColors?.find((c) => c.name === editProduct.color)?.id?.toString() ?? '',
            workshopCode: editProduct.workshopCode || '',
            costPrice: editCost,
            sellingPrice: toFormNumberString(editProduct.sellingPrice),
            supplier: editProduct.supplierId?.toString() || editProduct.supplier?.toString() || '',
            imagePath: editProduct.imagePath || '',
            hasThumbnail: editProduct.hasThumbnail === true,
          });
          initCostEntryFromPiece(editCost, editWeight);
          // Extract tag names from tags (which can be objects or strings)
          setSelectedTags(normalizeTags(editProduct.tags));

          // Clear pending File so a prior upload is not re-sent on save
          setImage(null);
          if (editProduct.imagePath) {
            setImageUrl(editProduct.imagePath);
          } else {
            setImageUrl('');
          }
        }
      } else {
        // Reset form for new product - only when opening
        if (isOpening) {
          setFormData({
            id: null,
            name: '',
            code: '',
            description: '',
            status: 'ACTIVE',
            type: '',
            material: '',
            weight: '',
            length: '',
            diameter: '',
            color: '',
            colorId: '',
            workshopCode: '',
            costPrice: '',
            sellingPrice: '',
            supplier: '',
            imagePath: '',
            hasThumbnail: false,
          });
          resetCostEntry();
          setSelectedTags([]);
          setImageUrl('');
          setImage(null);
          setCurrentStep(1);
          setFormProgress(20);
          previousEditProductId.current = null;
          previousPrefillDuplicateId.current = null;
        }
      }

      // Always reset confirmation dialogs and errors when opening
      if (isOpening) {
        setShowConfirmation(false);
        setShowCancelConfirmation(false);
        setImageError(null);
      }
    }

    // Update refs to track state for next render
    previousIsOpen.current = isOpen;
    if (!isOpen) {
      previousEditProductId.current = null;
      previousPrefillDuplicateId.current = null;
      setImage(null);
    }
  }, [
    isOpen,
    editProduct,
    prefillDuplicateFrom,
    initialProductColors,
    initialProductTypes,
    initialProductMaterials,
    initialProductSuppliers,
    initCostEntryFromPiece,
    resetCostEntry,
  ]);

  // Si la metadata (tipos/materiales/proveedores) llega después de abrir el duplicado, completar ids.
  useEffect(() => {
    if (!isOpen || !prefillDuplicateFrom) return;
    const src = prefillDuplicateFrom;

    setFormData((prev) => {
      if (prev.id != null) return prev;

      const patch: { type?: string; material?: string; supplier?: string } = {};

      if (!String(prev.type).trim()) {
        const typeName = typeof src.type === 'string' && src.type.trim() ? src.type.trim() : '';
        const typeByNameId =
          src.categoryId == null && typeName
            ? initialProductTypes?.find((t) => t.name.trim() === typeName)?.id
            : undefined;
        const typeValue =
          src.categoryId != null
            ? String(src.categoryId)
            : typeByNameId != null
              ? String(typeByNameId)
              : '';
        if (typeValue) patch.type = typeValue;
      }

      if (!String(prev.material).trim()) {
        const materialName =
          typeof src.material === 'string' && src.material.trim() ? src.material.trim() : '';
        const materialByNameId =
          src.materialId == null && materialName
            ? initialProductMaterials?.find((m) => m.name.trim() === materialName)?.id
            : undefined;
        const materialValue =
          src.materialId != null
            ? String(src.materialId)
            : materialByNameId != null
              ? String(materialByNameId)
              : '';
        if (materialValue) patch.material = materialValue;
      }

      if (!String(prev.supplier).trim()) {
        const supplierName =
          typeof src.supplier === 'string' && src.supplier.trim() ? src.supplier.trim() : '';
        const supplierByNameId =
          src.supplierId == null && supplierName
            ? initialProductSuppliers?.find((s) => s.name.trim() === supplierName)?.id
            : undefined;
        const supplierValue =
          src.supplierId != null
            ? String(src.supplierId)
            : supplierByNameId != null
              ? String(supplierByNameId)
              : '';
        if (supplierValue) patch.supplier = supplierValue;
      }

      if (Object.keys(patch).length === 0) return prev;
      return { ...prev, ...patch };
    });
  }, [
    isOpen,
    prefillDuplicateFrom,
    initialProductTypes,
    initialProductMaterials,
    initialProductSuppliers,
  ]);

  // Step navigation
  const setStep = useCallback((step: number) => {
    setCurrentStep(step);
    setFormProgress(Math.min(100, step * 20));
  }, []);

  const goNext = useCallback(() => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
      setFormProgress(Math.min(100, (currentStep + 1) * 20));
    }
  }, [currentStep]);

  const goPrev = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setFormProgress(Math.max(20, (currentStep - 1) * 20));
    }
  }, [currentStep]);

  // Form input handlers
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleSelectChange = useCallback((e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Tag management
  const handleSelectedTagsChange = useCallback((tags: ProductTag[]) => {
    const normalized = removeDuplicateTags(tags);
    if (normalized.length !== tags.length) {
      setTagError('Ya has agregado esta etiqueta');
      setTimeout(() => setTagError(null), 3000);
    } else {
      setTagError(null);
    }
    setSelectedTags(normalized);
  }, []);

  // Image handling functions
  const handleImageSelect = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const validateImageFile = useCallback((file: File): boolean => {
    // Check file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setImageError('El formato de archivo no es válido. Suba una imagen .jpeg, .jpg o .png');
      return false;
    }

    // Check file size (2MB = 2 * 1024 * 1024 bytes)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setImageError('La imagen es demasiado grande. El tamaño máximo permitido es 2MB');
      return false;
    }

    setImageError(null);
    return true;
  }, []);

  const applyImageFile = useCallback(
    (file: File) => {
      if (!validateImageFile(file)) {
        return;
      }

      setImage(file);
      setImageUrl(URL.createObjectURL(file));
    },
    [validateImageFile],
  );

  const handleImageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      applyImageFile(files[0]);
      e.target.value = '';
    },
    [applyImageFile],
  );

  const handleImageDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    imageDragCounterRef.current += 1;
    if (Array.from(e.dataTransfer.types).includes('Files')) {
      setIsDraggingImage(true);
    }
  }, []);

  const handleImageDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    imageDragCounterRef.current -= 1;
    if (imageDragCounterRef.current <= 0) {
      imageDragCounterRef.current = 0;
      setIsDraggingImage(false);
    }
  }, []);

  const handleImageDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleImageDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      imageDragCounterRef.current = 0;
      setIsDraggingImage(false);

      const file = e.dataTransfer.files?.[0];
      if (file) {
        applyImageFile(file);
      }
    },
    [applyImageFile],
  );

  const handleImageUpload = useCallback(async (): Promise<{
    imageUrl: string | null;
    hasThumbnail: boolean;
  }> => {
    if (!image) {
      return {
        imageUrl: formData.imagePath || null,
        hasThumbnail: formData.hasThumbnail,
      };
    }

    try {
      setIsUploading(true);

      const response = await UploadsService.uploadsControllerUploadProductImage({
        file: image,
      });

      return {
        imageUrl: response.imageUrl,
        hasThumbnail: response.hasThumbnail,
      };
    } catch (error) {
      console.error('Error uploading image:', error);
      setImageError('No se pudo cargar la imagen. Intente de nuevo.');
      return { imageUrl: null, hasThumbnail: false };
    } finally {
      setIsUploading(false);
    }
  }, [image, formData.imagePath, formData.hasThumbnail]);

  const handleRemoveImage = useCallback(() => {
    setImage(null);
    setImageUrl('');
    setIsDraggingImage(false);
    imageDragCounterRef.current = 0;
    setFormData((prev) => ({ ...prev, imagePath: '', hasThumbnail: false }));
  }, []);

  const handleImageClick = useCallback(() => {
    if (imageUrl) {
      setShowImageModal(true);
    }
  }, [imageUrl]);

  // Validation by step
  const validateCurrentStep = useCallback(() => {
    switch (currentStep) {
      case 1: // Basic Information
        return formData.name.trim() !== '';
      case 2: // Category
        return true;
      case 3: // Properties
        return true;
      case 4: // Costs
        return isPricingStepValid(formData, costPerGram, costEntryMode);
      case 5: // Supplier
        return true;
      default:
        return false;
    }
  }, [currentStep, formData, costPerGram, costEntryMode, isPricingStepValid]);

  // Handle save action
  const handleSave = useCallback(async () => {
    if (saveInFlightRef.current) {
      return;
    }
    saveInFlightRef.current = true;
    setIsSaving(true);

    try {
      const { imageUrl: imageUrlResult, hasThumbnail: uploadedHasThumb } =
        await handleImageUpload();

      const tagIds = tagArrayToIdList(selectedTags);

      const nextPath = imageUrlResult || formData.imagePath;
      const nextHasThumbnail = image ? uploadedHasThumb : formData.hasThumbnail;

      const productDataWithTags = {
        ...formData,
        color:
          colorCatalog.displayOptions.find((c) => c.id.toString() === formData.colorId)?.name ||
          formData.color ||
          '',
        tags: selectedTags,
        tagIds,
        imagePath: nextPath,
        ...(nextPath ? { hasThumbnail: nextHasThumbnail } : { hasThumbnail: false }),
      };

      await onSave(productDataWithTags);
      setShowConfirmation(false);
      onClose();
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      saveInFlightRef.current = false;
      setIsSaving(false);
    }
  }, [
    formData,
    selectedTags,
    image,
    handleImageUpload,
    onSave,
    onClose,
    colorCatalog.displayOptions,
  ]);

  // Handle confirmation and cancel dialogs
  const handleCloseClick = useCallback(() => {
    setShowCancelConfirmation(true);
  }, []);

  const cancelOperation = useCallback(() => {
    setShowCancelConfirmation(false);
    onClose();
  }, [onClose]);

  // Add Category Modal handlers
  const handleOpenAddCategoryModal = useCallback(() => {
    setShowAddCategoryModal(true);
  }, []);

  const handleCloseAddCategoryModal = useCallback(() => {
    setShowAddCategoryModal(false);
  }, []);

  const handleSaveCategory = useCallback(
    async (data: { nombre: string; descripcion: string }) => {
      try {
        // Generate code automatically from name
        const generatedCode =
          data.nombre
            .toUpperCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // Remove accents
            .replace(/[^A-Z0-9]/g, '') // Remove non-alphanumeric
            .substring(0, 20) || 'CATEGORY'; // Limit length and provide fallback

        const createData: CreateCategoryDto = {
          name: data.nombre,
          code: generatedCode,
          description: data.descripcion,
          status: CreateCategoryDto.status.ACTIVE,
          source: CreateCategoryDto.source.CATALOG,
        };

        const newCategory = await CategoriesService.categoriesControllerCreate(createData);

        // Update form data to select the newly created category
        if (newCategory && newCategory.id) {
          categoryCatalog.prependItems([{ id: newCategory.id, name: newCategory.name }]);
          setFormData((prev: any) => ({ ...prev, type: newCategory.id.toString() }));

          // Call callback to refresh categories list in parent component (background)
          if (onCategoryAdded) {
            onCategoryAdded(newCategory.id);
          }
        }

        setShowAddCategoryModal(false);
      } catch (error) {
        console.error('Error creating category:', error);
        throw error;
      }
    },
    [onCategoryAdded, categoryCatalog],
  );

  // Add Material Modal handlers
  const handleOpenAddMaterialModal = useCallback(() => {
    setShowAddMaterialModal(true);
  }, []);

  const handleCloseAddMaterialModal = useCallback(() => {
    setShowAddMaterialModal(false);
  }, []);

  const handleSaveMaterial = useCallback(
    async (data: { nombre: string; descripcion: string }) => {
      try {
        const createData: CreateMaterialDto = {
          name: data.nombre,
          description: data.descripcion,
          source: CreateMaterialDto.source.CATALOG,
        };

        const newMaterial = await MaterialsService.materialsControllerCreate(createData);

        // Update form data to select the newly created material
        if (newMaterial && newMaterial.id) {
          materialCatalog.prependItems([{ id: newMaterial.id, name: newMaterial.name }]);
          setFormData((prev: any) => ({ ...prev, material: newMaterial.id.toString() }));

          // Call callback to refresh materials list in parent component (background)
          if (onMaterialAdded) {
            onMaterialAdded(newMaterial.id);
          }
        }

        setShowAddMaterialModal(false);
      } catch (error) {
        console.error('Error creating material:', error);
        throw error;
      }
    },
    [onMaterialAdded, materialCatalog],
  );

  const handleOpenAddColorModal = useCallback(() => {
    setShowAddColorModal(true);
  }, []);

  const handleCloseAddColorModal = useCallback(() => {
    setShowAddColorModal(false);
  }, []);

  const handleSaveColor = useCallback(
    async (data: { nombre: string; descripcion: string }) => {
      try {
        const createData: CreateColorDto = {
          name: data.nombre,
          description: data.descripcion,
          source: CreateColorDto.source.CATALOG,
        };

        const newColor = await ColorsService.colorsControllerCreate(createData);

        if (newColor && newColor.id) {
          colorCatalog.prependItems([{ id: newColor.id, name: newColor.name }]);
          setFormData((prev: any) => ({
            ...prev,
            colorId: newColor.id.toString(),
            color: newColor.name,
          }));
          if (onColorAdded) {
            onColorAdded(newColor.id);
          }
        }

        setShowAddColorModal(false);
      } catch (error) {
        console.error('Error creating color:', error);
        throw error;
      }
    },
    [colorCatalog, onColorAdded],
  );

  // Add Supplier Modal handlers
  const handleOpenAddSupplierModal = useCallback(() => {
    setShowAddSupplierModal(true);
  }, []);

  const handleCloseAddSupplierModal = useCallback(() => {
    setShowAddSupplierModal(false);
  }, []);

  const handleSaveSupplier = useCallback(
    async (data: {
      nombre: string;
      contacto: string;
      telefono: string;
      email: string;
      descripcion: string;
    }) => {
      try {
        const createData: CreateSupplierDto = {
          name: data.nombre,
          contactPerson: data.contacto || undefined,
          phone: data.telefono || undefined,
          email: data.email?.trim() || undefined,
          description: data.descripcion || undefined,
          status: CreateSupplierDto.status.ACTIVE,
          source: CreateSupplierDto.source.CATALOG,
        };

        const newSupplier = await SuppliersService.suppliersControllerCreate(createData);

        // Update form data to select the newly created supplier
        if (newSupplier && newSupplier.id) {
          supplierCatalog.prependItems([{ id: newSupplier.id, name: newSupplier.name }]);
          setFormData((prev: any) => ({ ...prev, supplier: newSupplier.id.toString() }));

          // Call callback to refresh suppliers list in parent component (background)
          if (onSupplierAdded) {
            onSupplierAdded(newSupplier.id);
          }
        }

        setShowAddSupplierModal(false);
      } catch (error) {
        console.error('Error creating supplier:', error);
        throw error;
      }
    },
    [onSupplierAdded, supplierCatalog],
  );

  // Add Tag Modal handlers
  const handleOpenAddTagModal = useCallback(() => {
    setShowAddTagModal(true);
  }, []);

  const handleCloseAddTagModal = useCallback(() => {
    setShowAddTagModal(false);
  }, []);

  const handleSaveTag = useCallback(
    async (data: { nombre: string; descripcion: string; color: string }) => {
      try {
        const createData: CreateTagDto = {
          name: data.nombre,
          description: data.descripcion,
          color: data.color,
          source: CreateTagDto.source.CATALOG,
        };

        const newTag = await TagsService.tagsControllerCreate(createData);

        // Update local tags list and add to selected tags
        if (newTag && newTag.id) {
          tagsCatalog.prependItems([{ id: newTag.id, name: newTag.name, color: newTag.color }]);

          // Add to selected tags
          setSelectedTags((prev) =>
            removeDuplicateTags([
              ...prev,
              {
                id: newTag.id,
                name: newTag.name,
                color: newTag.color,
              },
            ]),
          );

          // Call callback to refresh tags list in parent component (background)
          if (onTagAdded) {
            onTagAdded(newTag.id);
          }
        }

        setShowAddTagModal(false);
      } catch (error) {
        console.error('Error creating tag:', error);
        throw error;
      }
    },
    [onTagAdded, tagsCatalog],
  );

  return {
    // Step management
    currentStep,
    formProgress,
    setStep,
    goNext,
    goPrev,

    // Form data
    formData,
    setFormData,
    selectedTags,
    setSelectedTags,

    // Cost entry
    costEntryMode,
    costPerGram,
    handleCostEntryModeChange,
    handleCostPriceChange,
    handleCostPerGramChange,
    handleWeightChange,
    hasWeight,

    // Image handling
    image,
    setImage,
    imageUrl,
    setImageUrl,
    imageError,
    setImageError,
    isUploading,
    setIsUploading,
    isDraggingImage,
    fileInputRef,

    // Image modal
    showImageModal,
    setShowImageModal,

    // UI state
    showConfirmation,
    setShowConfirmation,
    showCancelConfirmation,
    setShowCancelConfirmation,
    isSaving,
    setIsSaving,

    // Add Category Modal
    showAddCategoryModal,
    setShowAddCategoryModal,
    handleOpenAddCategoryModal,
    handleCloseAddCategoryModal,
    handleSaveCategory,

    // Add Material Modal
    showAddMaterialModal,
    setShowAddMaterialModal,
    handleOpenAddMaterialModal,
    handleCloseAddMaterialModal,
    handleSaveMaterial,
    showAddColorModal,
    setShowAddColorModal,
    handleOpenAddColorModal,
    handleCloseAddColorModal,
    handleSaveColor,

    // Add Supplier Modal
    showAddSupplierModal,
    setShowAddSupplierModal,
    handleOpenAddSupplierModal,
    handleCloseAddSupplierModal,
    handleSaveSupplier,

    // Add Tag Modal
    showAddTagModal,
    setShowAddTagModal,
    handleOpenAddTagModal,
    handleCloseAddTagModal,
    handleSaveTag,

    // Tag error state
    tagError,
    setTagError,

    categoryCatalog,
    tagsCatalog,
    materialCatalog,
    colorCatalog,
    supplierCatalog,

    // Handlers
    handleInputChange,
    handleSelectChange,
    handleSelectedTagsChange,
    handleImageSelect,
    handleImageChange,
    handleImageDragEnter,
    handleImageDragLeave,
    handleImageDragOver,
    handleImageDrop,
    handleImageUpload,
    handleRemoveImage,
    handleImageClick,
    validateCurrentStep,
    handleSave,
    handleCloseClick,
    cancelOperation,
  };
};
